import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../modules/auth';
import { addDoc, collection, doc, onSnapshot, serverTimestamp, getDoc } from 'firebase/firestore';
import db from '../../../../services/firebase';
import Drawer from 'react-modern-drawer';
import { ToastContainer } from 'react-toastify';
import { createTask } from 'app/Integrations/Linear';
import createClickUpService from 'app/Integrations/ClickUp';
import { showErrorToast, showSuccessToast } from 'app/components/ShowToast';
import { createShortcutIntegration } from 'app/Integrations/Shortcut';
import { createJiraTask } from 'app/Integrations/Jira';
import createSlackService from 'app/Integrations/Slack';
import Button from 'app/components/Button';

interface AddProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Task {
  title: string;
  description: string;
  owner: string;
  due_date: string;
  tenantId: string;
  created_at: string;
}

interface StoryData {
  project_id: string; 
  name: string;
  description?: string;
  story_type?: 'feature' | 'bug' | 'chore';
  priority?: 'urgent' | 'high' | 'medium' | 'normal' | 'low';
  dueDate?: string;
  status?: number; 
}

const AddTask: React.FC<AddProps> = ({ isOpen, onClose }) => {
  const { currentUser } = useAuth();
  const [dueDate, setDueDate] = useState('');
  const [taskName, setTaskName] = useState('');
  const [priority, setPriority] = useState('');
  const [controlTask, setControlTask] = useState('');
  const [testTask, setTestTask] = useState('');
  const [owners, setOwners] = useState<any[]>([]);
  const [owner, setOwner] = useState('');
  const [selectedOwnerEmail, setSelectedOwnerEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const taskNameRef = useRef<HTMLInputElement | null>(null);
  const [sendEmail, setSendEmail] = useState(false);
  const [description, setDescription] = useState('');
  const [integration, setIntegration] = useState<any[]>([]);
  const [sendLinearMessage, setSendLinearMessage] = useState(false);
  const [activeFrameworks, setActiveFrameworks] = useState<any[]>([]);
  const [sendClickUpMessage, setSendClickUpMessage] = useState(false);
  const [sendShortcutMessage, setSendShortcutMessage] = useState(false);
  const [sendJiraMessage, setSendJiraMessage] = useState(false);
  const [sendSlackMessage, setSendSlackMessage] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (taskNameRef.current) {
          taskNameRef.current.focus();
        }
      }, 300);
    }
  }, [isOpen]);

  useEffect(() => {
    const tenantId = currentUser?.tenantId || '';
    if (!tenantId) {
      return;
    }
    const tenantRef = doc(db, 'tenants', tenantId);
    const PolicyRef = collection(tenantRef, 'employees');

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      const risksData = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          uid: doc.data().uid,
          end_date: doc.data().end_date,
        }))
        .filter((data) => !data.end_date);
      setOwners(risksData);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    getIntegration();
    getActiveFramworks();
  }, []);

  const getIntegration = () => {
    try {
      const tenantId = currentUser?.tenantId || '';
      const tenantRef = doc(db, 'tenants', tenantId);
      const PolicyRef = collection(tenantRef, 'integrations');

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const integrationData = snapshot.docs.map((item) => ({
          ...item.data(),
          id: item.id,
          name: item.data().name,
        }));
        setIntegration(integrationData);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error getting integrations:', error);
    }
  };

  const getActiveFramworks = () => {
    try {
      const tenantId = currentUser?.tenantId || '';
      const tenantRef = doc(db, 'tenants', tenantId);
      const testsCollectionRef = collection(tenantRef, 'active_frameworks');

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const frameworksData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          frameworks: doc.data().frameworks,
        }));
        const frameworks = frameworksData[0]?.frameworks || [];
        setActiveFrameworks(frameworks);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error getting frameworks:', error);
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!taskName.trim()) {
      errors.taskName = 'Task Name is required';
    }

    if (!priority.trim()) {
      errors.priority = 'Task priority is required';
    }

    if (!owner.trim()) {
      errors.owner = 'Task Owner is required';
    }

    if (!dueDate.trim()) {
      errors.dueDate = 'Task DueDate is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    const selectedOwner = owners.find((o) => o.full_name === owner);
    const assigneeId = selectedOwner ? selectedOwner.uid : null;

    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    const day = currentDate.getDate();
    const todaysDate = `${month}/${day}/${year}`;

    try {
      if (currentUser) {
        const tenantRef = doc(db, 'tenants', currentUser.tenantId!);
        const tasksRef = collection(tenantRef, 'tasklists');
        await addDoc(tasksRef, {
          task_name: taskName.trim(),
          description: description,
          priority,
          owner,
          duedate: dueDate,
          control: controlTask,
          test: testTask,
          tenantId: currentUser?.tenantId,
          username: currentUser?.userName,
          date: todaysDate,
          checked: false,
          status: 'Incomplete',
          deliverable: {
            email: sendEmail,
            linear: sendLinearMessage,
            clickup: sendClickUpMessage,
            shortcut: sendShortcutMessage,
            jira: sendJiraMessage,
            slack: sendSlackMessage,
          },
          assignee_id: assigneeId,
          assignedBy:currentUser?.userName || '',
        });

        if (integration.length > 0) {
          // Filter Slack integration
          const slackIntegration = integration.find(
            (int) => int.name === 'Slack' && int.status === 'Connected'
          );

          // Filter Linear integration
          const linearIntegration = integration.find(
            (int) => int.name === 'Linear' && int.status === 'Connected'
          );

          // Filter ClickUp integration
          const clickUpIntegration = integration.find(
            (int) => int.name === 'ClickUp' && int.status === 'Connected'
          );

          const shortcutIntegration = integration.find(
            (int) => int.name === 'Shortcut' && int.status === 'Connected'
          );

          const jiraIntegration = integration.find(
            (int) => int.name === 'Jira' && int.status === 'Connected'
          );

          // Handle Slack notification
        if (slackIntegration && sendSlackMessage) {
          if (!slackIntegration.access_token || !slackIntegration.team_id) {
            showErrorToast({ message: 'Slack integration is missing access token or team ID' });
            return;
          }

          const slackService = createSlackService(slackIntegration.access_token, slackIntegration.team_id);
          const channelId = slackIntegration.selectedChannel;
          if (!channelId) {
            showErrorToast({ message: 'No Slack channel selected' });
            return;
          }

          if (!currentUser.tenantId) {
            showErrorToast({ message: 'Tenant ID is missing' });
            return;
          }

          setLoading(true);
          const joinResult = await slackService.joinChannel(channelId);
          if (!joinResult.success && joinResult.error !== 'already_in_channel') {
            setLoading(false);
            showErrorToast({ message: `Failed to join Slack channel: ${joinResult.error}` });
            return;
          }

          const task: Task = {
            title: taskName,
            description: description || 'No description provided',
            owner: owner,
            due_date: dueDate,
            tenantId: currentUser.tenantId,
            created_at: new Date().toISOString(),
          };

          const result = await slackService.sendTaskNotification(
            channelId,
            task,
            currentUser.tenantId,
            slackIntegration.id
          );
          setLoading(false);

          if (result.success) {
            // showSuccessToast({ message: 'Slack notification sent successfully' });
          } else {
            showErrorToast({
              message: `Error sending Slack notification`,
            });
          }
        }

          if (linearIntegration && sendLinearMessage) {
            const access_token = linearIntegration.access_token;
            const teamName = linearIntegration.teamName;
            createTask(access_token, teamName, taskName, description, priority, dueDate, selectedOwnerEmail)
              .then(() => showSuccessToast({ message: 'Linear Task created successfully' }))
              .catch(() => showErrorToast({ message: 'Error creating Linear task' }));
          }

          if (clickUpIntegration && sendClickUpMessage) {
            const access_token = clickUpIntegration.accessToken;
            const listId = clickUpIntegration.list_id;
            const clickUpService = createClickUpService(access_token);

            try {
              const tenantId = currentUser?.tenantId;
              const ownerEmail = selectedOwnerEmail || currentUser?.email;
              let clickUpUserId = '';
              if (tenantId && ownerEmail) {
                const accessReviewRef = doc(db, 'tenants', tenantId, 'access_review', 'clickup');
                const accessReviewSnap = await getDoc(accessReviewRef);
                if (accessReviewSnap.exists()) {
                  const data = accessReviewSnap.data();
                  const members = data.members || {};
                  for (const memberId in members) {
                    if (
                      members[memberId].email &&
                      members[memberId].email.toLowerCase() === ownerEmail.toLowerCase()
                    ) {
                      clickUpUserId = members[memberId].id?.toString() || '';
                      break;
                    }
                  }
                }
              }
              await clickUpService.createClickUpTask(listId, taskName, description, priority, dueDate, clickUpUserId)
                .then((response) => {
                  if (response.success) {
                    // showSuccessToast({ message: 'ClickUp task created successfully' });
                  } else {
                    showErrorToast({ message: 'Error creating ClickUp task' });
                  }
                })
                .catch((error) => console.error('Error creating ClickUp task:', error));
            } catch (err) {
              showErrorToast({ message: 'Error finding ClickUp user for task assignment' });
            }
          }

          // Handle Shortcut task creation
          if (shortcutIntegration && sendShortcutMessage) {
            const shortcutService = createShortcutIntegration({ apiToken: shortcutIntegration.apiToken });
            const storyData: StoryData = {
              project_id: shortcutIntegration.project_id,
              name: taskName,
              description: description,
              story_type: 'feature',
              priority: priority as 'urgent' | 'high' | 'medium' | 'normal' | 'low',
              dueDate: dueDate
                ? new Date(dueDate).toISOString().replace(/\.\d{3}Z$/, 'Z')
                : undefined,
            };
            const result = await shortcutService.createStory(storyData, shortcutIntegration.is2FAEnabled);
            if (result.success) {
              // showSuccessToast({ message: 'Shortcut story created successfully' });
            } else {
              showErrorToast({ message: 'Error creating Shortcut story' });
            }
          }

          // Handle Jira task creation
          if (jiraIntegration && sendJiraMessage) {
            const accessToken = jiraIntegration.accessToken;
            const cloudId = jiraIntegration.cloudId;
            const projectKey = jiraIntegration.selectedProject.key;
            const ownerEmail = selectedOwnerEmail || currentUser?.email;
            let assigneeId = '';
            const tenantId = currentUser?.tenantId || '';

            if (cloudId && projectKey) {
              const accessReviewRef = doc(db, 'tenants', tenantId, 'access_review', 'jira');
              const accessReviewSnap = await getDoc(accessReviewRef);
              if (accessReviewSnap.exists()) {
                const data = accessReviewSnap.data();
                const members = data.members || {};
                for (const memberId in members) {
                  if (
                    members[memberId].emailAddress &&
                    members[memberId].emailAddress.toLowerCase() === ownerEmail?.toLowerCase()
                  ) {
                    assigneeId = members[memberId].accountId?.toString() || '';
                    break;
                  }
                }
              }
            }
            createJiraTask(projectKey, taskName, description, priority, dueDate, accessToken, cloudId, assigneeId)
              .then((response) => {
                if (response.success) {
                  // showSuccessToast({ message: 'Jira task created successfully' });
                } else {
                  showErrorToast({ message: `Error creating Jira task: ${response.error || 'Unknown error'}` });
                }
              })
              .catch((error) => {
                console.error('Error creating Jira task:', error);
                showErrorToast({ message: 'Error creating Jira task' });
              });
          }
        }

        const activitiesRef = collection(tenantRef, 'activities');
        await addDoc(activitiesRef, {
          message: `${currentUser?.userName} added a new task "${taskName.trim()}" for ${owner}`,
          timestamp: serverTimestamp(),
          tenantId: currentUser?.tenantId,
          uid: currentUser?.uid,
          username: currentUser?.userName,
          action: 'Task added',
          target: 'Tasks',
          email: currentUser?.email,
          date: todaysDate,
        });
        showSuccessToast({ message: 'Task added successfully' });
        setTaskName('');
        setPriority('');
        setOwner('');
        setControlTask('');
        setTestTask('');
        setDueDate('');
        setDescription('');
        setSendEmail(false);
        setSendLinearMessage(false);
        setSendClickUpMessage(false);
        setSendShortcutMessage(false);
        setSendJiraMessage(false);
        setSendSlackMessage(false);
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Error adding task:', error);
      showErrorToast({ message: 'Error adding task' });
    }

    setLoading(false);
  };

  const handleTasknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTaskName(e.target.value);
    setFormErrors((prev) => ({ ...prev, taskName: '' }));
  };

  const onCancel = () => {
    setFormErrors({});
    setSendEmail(false);
    setSendLinearMessage(false);
    setSendClickUpMessage(false);
    setSendShortcutMessage(false);
    setSendJiraMessage(false);
    setSendSlackMessage(false);
    setOwner('');
    setSelectedOwnerEmail('');
    onClose();
  };

  return (
    <>
      <Drawer open={isOpen} onClose={onCancel} direction='right' className='' size='45%'>
        <div
          className='modal-dialog-centered border-bottom border-gray-300'
          style={{
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            padding: '14px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            paddingLeft: '15px',
            paddingRight: '12px',
          }}
        >
          <div className='fs-4 fw-bold mx-2' style={{ margin: 0 }}>
            Add Task
          </div>
          <div style={{ display: 'flex', gap: '16px' }}>
            <div
              className='btn btn-icon btn-sm btn-active-light-primary'
              onClick={onCancel}
              aria-label='Close'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='20'
                height='20'
                viewBox='0 0 24 24'
                fill='none'
                stroke='#5A5A5A'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-x'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M18 6l-12 12' />
                <path d='M6 6l12 12' />
              </svg>
            </div>
          </div>
        </div>
        <div
        className='scroll-y overflow-auto p-6 mb-10'
        tabIndex={-1}
        style={{
          height: 'calc(100vh - 120px)',
          paddingBottom: '80px',
        }}
      >
          <form id='kt_risk_form' className='form' onSubmit={handleSubmit}>
            <div className='modal-content'>
              <div className='modal-body'>
                <div className='fs-6 mb-4'>
                  Delegate tasks which might help you fix your controls and tests to your team
                  members. You can also add due dates to ensure audit readiness.
                </div>
                <div className='fv-row mb-4'>
                  <label className='fw-semibold fs-6 mb-1 required'>Task Name</label>
                  <input
                    placeholder='Enter Task Name'
                    type='text'
                    className={`form-control ${formErrors.taskName ? 'is-invalid' : ''}`}
                    name='taskname'
                    value={taskName}
                    onChange={handleTasknameChange}
                    autoComplete='off'
                    ref={taskNameRef}
                  />
                  {formErrors.taskName && (
                    <div className='invalid-feedback'>{formErrors.taskName}</div>
                  )}
                </div>
                <div className='fv-row mb-4'>
                  <label className='fw-bold fs-6 mb-2'>Description</label>
                  <textarea
                    placeholder='Enter description for task'
                    className='form-control mb-3 mb-lg-0 w-100'
                    name='description'
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    autoComplete='off'
                  />
                </div>
                <div className='fv-row mb-4'>
                  <label className='fw-semibold fs-6 mb-1 required'>Priority</label>
                  <select
                    className={`form-select ${formErrors.priority ? 'is-invalid' : ''}`}
                    value={priority}
                    onChange={(e) => {
                      setPriority(e.target.value);
                      setFormErrors((prev) => ({ ...prev, priority: '' }));
                    }}
                  >
                    <option value='' disabled>
                      Select Priority
                    </option>
                    <option value='Low'>Low</option>
                    <option value='Medium'>Medium</option>
                    <option value='High'>High</option>
                  </select>
                  {formErrors.priority && (
                    <span className='invalid-feedback'>{formErrors.priority}</span>
                  )}
                </div>
                <div className='fv-row'>
                  <label className='fw-semibold fs-6 mb-1 required'>Assign to</label>
                  <select
                    data-placeholder='Select Owner'
                    onChange={(e) => {
                      const [selectedFullName, selectedEmail] = e.target.value.split('|');
                      setOwner(selectedFullName);
                      setSelectedOwnerEmail(selectedEmail);
                      setFormErrors((prev) => ({ ...prev, owner: '' }));
                    }}
                    value={owner ? `${owner}|${selectedOwnerEmail}` : ''}
                    className={`form-select ${formErrors.owner ? 'is-invalid' : ''}`}
                  >
                    <option value='' disabled>
                      Select Owner
                    </option>
                    {owners
                      .filter((user) => user.role === 'Admin' || user.role === 'Owner')
                      .map((owner) => (
                        <option key={owner.id} value={`${owner.full_name}|${owner.email}`}>
                          {owner.full_name}
                        </option>
                      ))}
                  </select>
                  {formErrors.owner && <span className='invalid-feedback'>{formErrors.owner}</span>}
                </div>
                <div className='form-group mb-4'>
                  <label className='fw-semibold text-dark mt-5 fs-6 mb-1 required'>Due date</label>
                  <input
                    type='date'
                    className={`form-control ${formErrors.dueDate ? 'is-invalid' : ''}`}
                    placeholder='Select due date'
                    name='dueDate'
                    value={dueDate}
                    onChange={(e) => {
                      setDueDate(e.target.value);
                      setFormErrors((prev) => ({ ...prev, dueDate: '' }));
                    }}
                  />
                  {formErrors.dueDate && (
                    <span className='invalid-feedback'>{formErrors.dueDate}</span>
                  )}
                </div>
                <div className='separator my-5'></div>
                <div className='form-group'>
                  <label className='fw-semibold text-dark fs-6 mb-4'>Delivery</label>
                  <div className='checkbox-group'>
                    <div className='form-check mb-4'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id='sendEmailCheckbox'
                        checked={sendEmail}
                        onChange={(e) => setSendEmail(e.target.checked)}
                      />
                      <label className='text-dark ms-2 fs-6' htmlFor='sendEmailCheckbox'>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='20'
                          height='20'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='#5A5A5A'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          className='icon icon-tabler icons-tabler-outline icon-tabler-mail me-1 mb-1'
                        >
                          <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                          <path d='M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z' />
                          <path d='M3 7l9 6l9 -6' />
                        </svg>
                        Send email
                      </label>
                    </div>
                    {activeFrameworks.length > 0 &&
                      currentUser?.account_type !== 'Trial' &&
                      currentUser?.account_type === 'internal' && (
                        <>
                          {integration.map((int) => {
                            switch (int.name) {
                              case 'Linear':
                                return (
                                  <div className='form-check mb-4' key={int.id}>
                                    <input
                                      className='form-check-input'
                                      type='checkbox'
                                      id='sendLinearMessageCheckbox'
                                      checked={sendLinearMessage}
                                      onChange={(e) => setSendLinearMessage(e.target.checked)}
                                      disabled={!int.status || int.status !== 'Connected'}
                                    />
                                    <label
                                      className='text-dark ms-3 fs-6'
                                      htmlFor='sendLinearMessageCheckbox'
                                    >
                                      <img
                                        src='/integrations/linear.svg'
                                        width={16}
                                        className='me-1'
                                        alt=''
                                      />{' '}
                                      Create issue on Linear{' '}
                                      {int.teamName ? (
                                        <span
                                          className='badge fs-7'
                                          style={{
                                            color: int.status === 'Connected' ? '#0E9F6E' : '#FF0000',
                                            background:
                                              int.status === 'Connected' ? '#E8F8F4' : '#FFCCCC',
                                          }}
                                        >
                                          {int.status}
                                        </span>
                                      ) : (
                                        <span
                                          className='badge fs-7 badge-warning'
                                          style={{ cursor: 'pointer' }}
                                          onClick={() =>
                                            (window.location.href = `/integrations?id=${int.id}&service=Linear`)
                                          }
                                        >
                                          Select a team
                                        </span>
                                      )}
                                    </label>
                                  </div>
                                );
                              case 'ClickUp':
                                return (
                                  <div className='form-check mb-4' key={int.id}>
                                    <input
                                      className='form-check-input'
                                      type='checkbox'
                                      id='sendClickUpTaskCheckbox'
                                      checked={sendClickUpMessage}
                                      onChange={(e) => setSendClickUpMessage(e.target.checked)}
                                      disabled={!int.status || int.status !== 'Connected'}
                                    />
                                    <label
                                      className='text-dark ms-1 fs-6'
                                      htmlFor='sendClickUpTaskCheckbox'
                                    >
                                      <img
                                        src='/integrations/CLickUp.svg'
                                        width={25}
                                        className=''
                                        alt=''
                                      />{' '}
                                      Create task on ClickUp{' '}
                                      {int.workspace_name ? (
                                        <span
                                          className='badge fs-7'
                                          style={{
                                            color: int.status === 'Connected' ? '#0E9F6E' : '#FF0000',
                                            background:
                                              int.status === 'Connected' ? '#E8F8F4' : '#FFCCCC',
                                          }}
                                        >
                                          {int.status}
                                        </span>
                                      ) : (
                                        <span
                                          className='badge fs-7 badge-warning'
                                          style={{ cursor: 'pointer' }}
                                          onClick={() =>
                                            (window.location.href = `/integrations?id=${int.id}&service=ClickUp`)
                                          }
                                        >
                                          Select a workspace
                                        </span>
                                      )}
                                    </label>
                                  </div>
                                );
                              case 'Shortcut':
                                return (
                                  <div className='form-check mb-4' key={int.id}>
                                    <input
                                      className='form-check-input'
                                      type='checkbox'
                                      id='sendShortcutTaskCheckbox'
                                      checked={sendShortcutMessage}
                                      onChange={(e) => setSendShortcutMessage(e.target.checked)}
                                      disabled={!int.status || int.status !== 'Connected'}
                                    />
                                    <label
                                      className='text-dark ms-2 fs-6'
                                      htmlFor='sendShortcutTaskCheckbox'
                                    >
                                      <img
                                        src='/integrations/Shortcut.avif'
                                        width={18}
                                        className='me-1'
                                        alt=''
                                      />{' '}
                                      Create Shortcut Story{' '}
                                      {int.project_id ? (
                                        <span
                                          className='badge fs-7'
                                          style={{
                                            color: int.status === 'Connected' ? '#0E9F6E' : '#FF0000',
                                            background:
                                              int.status === 'Connected' ? '#E8F8F4' : '#FFCCCC',
                                          }}
                                        >
                                          {int.status}
                                        </span>
                                      ) : (
                                        <span
                                          className='badge fs-7 badge-warning'
                                          style={{ cursor: 'pointer' }}
                                          onClick={() =>
                                            (window.location.href = `/integrations?id=${int.id}&service=Shortcut`)
                                          }
                                        >
                                          Select a project
                                        </span>
                                      )}
                                    </label>
                                  </div>
                                );
                              case 'Jira':
                                return (
                                  <div className='form-check mb-4' key={int.id}>
                                    <input
                                      className='form-check-input'
                                      type='checkbox'
                                      id='sendJiraTicketCheckbox'
                                      checked={sendJiraMessage}
                                      onChange={(e) => setSendJiraMessage(e.target.checked)}
                                      disabled={!int.status || int.status !== 'Connected'}
                                    />
                                    <label
                                      className='text-dark ms-2 fs-6'
                                      htmlFor='sendJiraTicketCheckbox'
                                    >
                                      <img
                                        src='/integrations/JIra.svg'
                                        width={18}
                                        className='me-1'
                                        alt=''
                                      />{' '}
                                      Create Jira Ticket{' '}
                                      {int.selectedProject?.key ? (
                                        <span
                                          className='badge fs-7'
                                          style={{
                                            color: int.status === 'Connected' ? '#0E9F6E' : '#FF0000',
                                            background:
                                              int.status === 'Connected' ? '#E8F8F4' : '#FFCCCC',
                                          }}
                                        >
                                          {int.status}
                                        </span>
                                      ) : (
                                        <span
                                          className='badge fs-7 badge-warning'
                                          style={{ cursor: 'pointer' }}
                                          onClick={() =>
                                            (window.location.href = `/integrations?id=${int.id}&service=Jira`)
                                          }
                                        >
                                          Select a project
                                        </span>
                                      )}
                                    </label>
                                  </div>
                                );
                              case 'Slack':
                                return (
                                  <div className='form-check mb-4' key={int.id}>
                                    <input
                                      className='form-check-input'
                                      type='checkbox'
                                      id='sendSlackMessageCheckbox'
                                      checked={sendSlackMessage}
                                      onChange={(e) => setSendSlackMessage(e.target.checked)}
                                      disabled={!int.status || int.status !== 'Connected' || !int.selectedChannel}
                                    />
                                    <label
                                      className='text-dark ms-2 fs-6'
                                      htmlFor='sendSlackMessageCheckbox'
                                    >
                                      <img
                                        src='/integrations/Slack.svg'
                                        width={18}
                                        className='me-1'
                                        alt=''
                                      />{' '}
                                      Send Slack Notification{' '}
                                      {int.selectedChannel ? (
                                        <span
                                          className='badge fs-7'
                                          style={{
                                            color: int.status === 'Connected' ? '#0E9F6E' : '#FF0000',
                                            background:
                                              int.status === 'Connected' ? '#E8F8F4' : '#FFCCCC',
                                          }}
                                        >
                                          {int.status}
                                        </span>
                                      ) : (
                                        <span
                                          className='badge fs-7 badge-warning'
                                          style={{ cursor: 'pointer' }}
                                          onClick={() =>
                                            (window.location.href = `/integrations?id=${int.id}&service=Slack`)
                                          }
                                        >
                                          Select a channel
                                        </span>
                                      )}
                                    </label>
                                  </div>
                                );
                              default:
                                return null;
                            }
                          })}
                        </>
                      )}
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div
          className="offcanvas-footer d-flex justify-content-end border-top py-4 px-6"
          style={{ position: "sticky", bottom: 0, backgroundColor: "white", zIndex: 5 }}
        >
          <div className='d-flex justify-content-end align-items-center gap-3'>
            <Button
              btnType='secondary'
              onClick={onCancel}
            >
              Cancel
            </Button>
            {loading ? (
              <Button
                btnType='primary'
                disabled
              >
                Adding Task...
                <span className='spinner-border spinner-border-sm align-middle'></span>
              </Button>
            ) : (
              <Button
                btnType='primary'
                onClick={handleSubmit}
              >
                Add Task
              </Button>
            )}
          </div>
        </div>
      </Drawer>
      <ToastContainer />
    </>
  );
};

export default AddTask;